import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
// Controllers
import { AlertController, LoadingController } from '@ionic/angular';
// Models
import { droneIcon, droneMoccaIcon } from 'src/assets/icons';
import { DroneOperator, User, Pilot, Vehicle } from '@mocca-technologies/model';
// Services
import { DroneOperatorService } from 'src/app/services/drone-operator.service';
import { FilesService } from 'src/app/services/files.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { LoadingGuardService } from 'src/app/services/loading-guard.service';

@Component({
  selector: 'app-sub-operator-info-card',
  templateUrl: './sub-operator-info-card.component.html',
  styleUrls: ['./sub-operator-info-card.component.scss'],
})
export class SubOperatorInfoCardComponent implements OnInit, OnDestroy, OnChanges {
  user: User;

  vehicleList: Vehicle[] = [];
  pilotList: Pilot[] = [];

  droneIcon = droneIcon;
  droneMoccaIcon = droneMoccaIcon;

  @Input() operator: DroneOperator;
  @Input() isSummary = false;
  @Input() selectedVehicle: Vehicle;
  @Input() selectedPilot: Pilot;

  @Output() selectPilotGlobally = new EventEmitter<Pilot>();
  @Output() selectVehicleGlobally = new EventEmitter<Vehicle>();
  @Output() refreshPilotVehicleLists = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  private chain: Promise<void> = Promise.resolve();

  get isAdminDevel(): boolean {
    return this.user?.profile === 'admin' || this.user?.profile === 'devel';
  }

  constructor(
    private loadingController: LoadingController,
    private cdr: ChangeDetectorRef,
    private droneOperatorService: DroneOperatorService,
    private alertController: AlertController,
    private filesService: FilesService,
    private httpResponseService: HttpResponsesService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.user = localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user')!)
      : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When operator changes, refresh BOTH lists with ONE loader (no overlapping loaders)
    if (changes['operator'] && this.operator?.uniqueId && !this.isSummary) {
      this.chain = this.chain.then(() => this.loadAll()).catch(() => {});
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.revokeBlobUrl(this.operator?.avatarUrl);
    this.revokeBlobUrl(this.selectedPilot?.avatarUrl);
    this.revokeBlobUrl(this.selectedVehicle?.avatarUrl);
  }

  // -------------------- helpers --------------------

  private t(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  private revokeBlobUrl(url?: string | null): void {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  }

  private filterOutMainOrg<T extends { organizationUuid?: string }>(list: T[]): T[] {
    if (!this.isAdminDevel) return list;
    return (list || []).filter((x) => x.organizationUuid !== this.user?.mainOrganizationUuid);
  }

  private async safeDismissLoading(loading?: HTMLIonLoadingElement): Promise<void> {
    if (!loading) return;
    try { await loading.dismiss(); } catch {}
  }

  /**
   * Single combined load that prevents stacked loaders.
   */
  private async loadAll(): Promise<void> {
    const loading = await this.loadingController.create({
      message: this.t('COMMON.LOADING.INFO'),
      spinner: 'bubbles',
    });

    await loading.present();

    try {
      const [pRes, vRes] = await Promise.all([
        firstValueFrom(this.droneOperatorService.getAllPilots(this.operator.uniqueId).pipe(takeUntil(this.destroy$))),
        firstValueFrom(this.droneOperatorService.getAllVehicles(this.operator.uniqueId).pipe(takeUntil(this.destroy$))),
      ]);

      this.pilotList = this.filterOutMainOrg((pRes?.body?.responseObject || []) as Pilot[]);
      this.vehicleList = this.filterOutMainOrg((vRes?.body?.responseObject || []) as Vehicle[]);

      this.cdr.detectChanges();
    } catch (error: any) {
      this.httpResponseService.showServerError(error?.status, error?.error);
    } finally {
      await this.safeDismissLoading(loading);
    }
  }

  /**
   * Public reload methods (kept for feature parity)
   */
  async loadPilots(): Promise<void> {
    this.chain = this.chain.then(() => this.loadAll()).catch(() => {});
  }

  async loadVehicles(): Promise<void> {
    this.chain = this.chain.then(() => this.loadAll()).catch(() => {});
  }

   /**
   * ============================================================
   * ======================= DELETE METHODS ======================
   * ============================================================
   */

  /**
   * Deletes operator avatar from backend
   */
  private deleteOperatorAvatar() {
    this.filesService
      .deleteOperatorAvatar(this.operator.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.status === 200) {
            this.revokeBlobUrl(this.operator.avatarUrl);
            this.operator.avatarUrl = null;
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.httpResponseService.showServerError(error?.status, error?.error);
        },
      });
  }

  /**
   * Delete vehicle from sub-operator
   */
  async deleteVehicle(vehicle: Vehicle) {
    const confirmed = await this.confirm(
      this.t('PAGES.FLEET.DELETE_VEHICLE_FROM_SUBOPERATOR_HEADER', {
        vehicle: vehicle.plate,
      }),
      this.t('PAGES.FLEET.DELETE_VEHICLE_FROM_SUBOPERATOR_MESSAGE', {
        vehicle: vehicle.plate,
        suboperator: this.operator?.organizationName,
      }),
    );

    if (!confirmed) return;

    this.droneOperatorService
      .deleteVehicleFromOperator(vehicle.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.status === 200) this.refreshAfterChange({ vehicles: true });
        },
        error: (error) => {
          this.httpResponseService.showServerError(error?.status, error?.error);
        },
      });
  }

  /**
   * Delete pilot from sub-operator
   */
  async deletePilot(pilot: Pilot) {
    const name = pilot?.contactInformation?.name;

    const confirmed = await this.confirm(
      this.t('PAGES.FLEET.DELETE_PILOT_FROM_SUBOPERATOR_HEADER', { pilot: name }),
      this.t('PAGES.FLEET.DELETE_PILOT_FROM_SUBOPERATOR_MESSAGE', {
        pilot: name,
        suboperator: this.operator?.organizationName,
      }),
    );

    if (!confirmed) return;

    this.droneOperatorService
      .deleteUserFromOperator(pilot.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.status === 200) this.refreshAfterChange({ pilots: true });
        },
        error: (error) => {
          this.httpResponseService.showServerError(error?.status, error?.error);
        },
      });
  }

  /**
   * Centralized refresh logic after a successful mutation (add/remove pilot/vehicle, etc).
   * - Reloads local sub-operator lists (pilots/vehicles) without stacking loaders
   * - Notifies parent (Fleet) to refresh global lists/maps once
   */
  private refreshAfterChange(opts?: { pilots?: boolean; vehicles?: boolean }): void {
    // Tell parent to refresh the "petition once" data (main lists + maps)
    this.refreshPilotVehicleLists.emit(true);

    // Reload local lists in THIS card (no overlapping loaders)
    // Since your loadAll loads both, we just call loadAll once.
    this.chain = this.chain
      .then(async () => {
        if (this.destroy$.closed) return;
        await this.loadAll();
      })
      .catch(() => {});
  }

  // -------------------- selection --------------------

  /**
   * Select pilot inside sub-operator info.
   */
  selectPilot(pilot: Pilot): void {
    if (pilot?.uniqueId === this.selectedPilot?.uniqueId) {
      this.selectedPilot = null;
      this.selectPilotGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    this.filesService
      .getPilotAvatar(pilot.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || blob.size === 0) {
            pilot.avatarUrl = null;
          } else {
            this.revokeBlobUrl(pilot.avatarUrl);
            pilot.avatarUrl = URL.createObjectURL(blob);
          }

          this.selectedPilot = pilot;
          this.selectPilotGlobally.emit(pilot);
          this.cdr.detectChanges();
        },
        error: (error) => {
          if (error?.status !== 422) {
            this.httpResponseService.showServerError(error?.status, error?.error);
          }
          pilot.avatarUrl = null;
          this.selectedPilot = pilot;
          this.selectPilotGlobally.emit(pilot);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Select vehicle inside sub-operator info.
   */
  selectVehicle(vehicle: Vehicle): void {
    if (vehicle?.uniqueId === this.selectedVehicle?.uniqueId) {
      this.selectedVehicle = null;
      this.selectVehicleGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    this.filesService
      .getDroneImage(vehicle.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || blob.size === 0) {
            vehicle.avatarUrl = null;
          } else {
            this.revokeBlobUrl(vehicle.avatarUrl);
            vehicle.avatarUrl = URL.createObjectURL(blob);
          }

          this.selectedVehicle = vehicle;
          this.selectVehicleGlobally.emit(vehicle);
          this.cdr.detectChanges();
        },
        error: (error) => {
          if (error?.status !== 422) {
            this.httpResponseService.showServerError(error?.status, error?.error);
          }
          vehicle.avatarUrl = null;
          this.selectedVehicle = vehicle;
          this.selectVehicleGlobally.emit(vehicle);
          this.cdr.detectChanges();
        },
      });
  }

async changeLogo() {
    const hasAvatar = !!this.operator?.avatarUrl;

    if (hasAvatar) {
      const actionSheet = await this.alertController.create({
        header: this.t('PAGES.FLEET.AVATAR_OPTIONS_HEADER'),
        message: this.t('PAGES.FLEET.AVATAR_OPTIONS_MESSAGE'),
        buttons: [
          { text: this.t('PAGES.FLEET.CHANGE_IMAGE'), handler: () => this.selectNewImage() },
          { text: this.t('PAGES.FLEET.DELETE_IMAGE'), role: 'destructive', handler: () => this.deleteOperatorAvatar() },
          { text: this.t('COMMON.BUTTONS.CANCEL'), role: 'cancel' },
        ],
      });

      await actionSheet.present();
    } else {
      this.selectNewImage();
    }
  }

  selectNewImage() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.httpResponseService.showServerError('400', 'Please select a valid image file.');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.operator.avatarUrl = reader.result as string;
        this.cdr.detectChanges();

        this.filesService
          .uploadOperatorAvatar(file, file.name, this.operator)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res?.status !== 200) {
                this.operator.avatarUrl = null;
                this.cdr.detectChanges();
              }
            },
            error: (error) => {
              this.httpResponseService.showServerError(error?.status, error?.error);
              this.operator.avatarUrl = null;
              this.cdr.detectChanges();
            },
          });
      };

      reader.readAsDataURL(file);
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  onAvatarError(operator: DroneOperator) {
    this.revokeBlobUrl(operator?.avatarUrl);
    operator.avatarUrl = null;
    this.cdr.detectChanges();
  }

  // -------------------- drag & drop (kept) --------------------

  async onVehicleDropped(event: CdkDragDrop<Vehicle[]>): Promise<void> {
    if (event.previousContainer === event.container) return;

    const vehicle = event.item?.data;
    if (!vehicle) return;

    if (this.vehicleList.some((v) => v.uniqueId === vehicle.uniqueId)) {
      const alert = await this.alertController.create({
        header: this.t('PAGES.FLEET.DUPLICATE_VEHICLE_SUBOPERATOR_HEADER'),
        message: this.t('PAGES.FLEET.DUPLICATE_VEHICLE_SUBOPERATOR_MESSAGE', {
          suboperator: this.operator?.organizationName,
        }),
        buttons: [this.t('COMMON.BUTTONS.OK')],
      });
      return alert.present();
    }

    const confirmed = await this.confirm(
      this.t('PAGES.FLEET.CONFIRM_VEHICLE_SUBOPERATOR_HEADER'),
      this.t('PAGES.FLEET.CONFIRM_VEHICLE_SUBOPERATOR_MESSAGE', {
        vehicle: vehicle.plate || vehicle.uniqueId,
        suboperator: this.operator?.organizationName,
      }),
    );

    if (!confirmed) return;

    this.droneOperatorService
      .addVehicleToOperator(this.operator.uniqueId, vehicle.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.status === 200) {
            this.refreshAfterChange({ vehicles: true });
          }
        },
        error: (error) => this.httpResponseService.showServerError(error?.status, error?.error),
      });
  }

  async onPilotDropped(event: CdkDragDrop<Pilot[]>): Promise<void> {
    if (event.previousContainer === event.container) return;

    const pilot = event.item?.data;
    if (!pilot) return;

    if (this.pilotList.some((p) => p.uniqueId === pilot.uniqueId)) {
      const alert = await this.alertController.create({
        header: this.t('PAGES.FLEET.DUPLICATE_PILOT_SUBOPERATOR_HEADER'),
        message: this.t('PAGES.FLEET.DUPLICATE_PILOT_SUBOPERATOR_MESSAGE', {
          suboperator: this.operator?.organizationName,
        }),
        buttons: [this.t('COMMON.BUTTONS.OK')],
      });
      return alert.present();
    }

    const confirmed = await this.confirm(
      this.t('PAGES.FLEET.CONFIRM_PILOT_SUBOPERATOR_HEADER'),
      this.t('PAGES.FLEET.CONFIRM_PILOT_SUBOPERATOR_MESSAGE', {
        pilot: pilot.contactInformation?.name || pilot.uniqueId,
        suboperator: this.operator?.organizationName,
      }),
    );

    if (!confirmed) return;

    this.droneOperatorService
      .addUserToOperator(this.operator.uniqueId, pilot.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.status === 200) {
            this.refreshAfterChange({ pilots: true });
          }
        },
        error: (error) => this.httpResponseService.showServerError(error?.status, error?.error),
      });
  }

  // -------------------- dialogs --------------------

  private async confirm(header: string, message: string): Promise<boolean> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header,
        message,
        buttons: [
          { text: this.t('COMMON.BUTTONS.CANCEL'), role: 'cancel', handler: () => resolve(false) },
          { text: this.t('COMMON.BUTTONS.YES'), handler: () => resolve(true) },
        ],
      });
      await alert.present();
    });
  }
}
